import React, {useState} from 'react';
import {
    Box,
    Button,
    Chip,
    Divider,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import LanguageIcon from '@mui/icons-material/Language';
import LabelIcon from '@mui/icons-material/Label';
import AbcIcon from '@mui/icons-material/Abc';
import TabLoading from './TabLoading';
import TabEmpty from './TabEmpty';
import AliasEditorDialog from './AliasEditorDialog';
import AliasDeleteDialog from './AliasDeleteDialog';
import {usePagination} from './usePagination';
import TabPagination from './TabPagination';

const SOURCE_TYPE_META = {
    PDF:    {label: 'PDF',    icon: PictureAsPdfIcon, color: 'error'},
    AUDIO:  {label: 'Audio',  icon: AudioFileIcon,    color: 'secondary'},
    VIDEO:  {label: 'Video',  icon: VideoFileIcon,    color: 'info'},
    OSINT:  {label: 'Web',    icon: LanguageIcon,     color: 'primary'},
    FINTEL: {label: 'FINTEL', icon: ArticleIcon,      color: 'success'},
};

function getSourceMeta(type) {
    return SOURCE_TYPE_META[(type || '').toUpperCase()] || {label: type || 'Document', icon: ArticleIcon, color: 'default'};
}

const KnownAliasRow = ({
    alias,
    crudEnabled,
    saving,
    onDeleteClick,
    pinEnabled,
    isPinned,
    atPinCap,
    onPinAlias,
    onUnpinAlias,
}) => (
    <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        justifyContent="space-between"
        sx={{px: 2, py: 1}}
    >
        <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
            <AbcIcon sx={{color: 'text.disabled', fontSize: 18, flexShrink: 0}}/>
            <Typography fontWeight={500} sx={{minWidth: 0}}>
                {alias}
            </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
            {pinEnabled ? (
                <Button
                    size="small"
                    variant="text"
                    onClick={() => (isPinned ? onUnpinAlias?.(alias) : onPinAlias?.(alias))}
                    disabled={!isPinned && atPinCap}
                >
                    {isPinned ? 'Unpin' : 'Pin to card'}
                </Button>
            ) : null}
            {crudEnabled && (
                <Button
                    size="small"
                    color="error"
                    variant="text"
                    onClick={() => onDeleteClick(alias)}
                    disabled={saving}
                >
                    Delete
                </Button>
            )}
        </Stack>
    </Stack>
);

const SurfaceFormRow = ({form}) => (
    <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{px: 2, py: 1}}
    >
        <LabelIcon sx={{color: 'text.disabled', fontSize: 18, flexShrink: 0}}/>
        <Typography fontWeight={500} sx={{minWidth: 0, flexShrink: 0}}>
            {form.text}
        </Typography>
        <Tooltip title={`${form.count} total mention${form.count !== 1 ? 's' : ''} in corpus`} arrow>
            <Chip
                label={`×${form.count}`}
                size="small"
                variant="outlined"
                sx={{fontSize: 11, height: 20, cursor: 'default'}}
            />
        </Tooltip>
        {(form.sourceTypes || []).map(st => {
            const {label, color} = getSourceMeta(st);
            return (
                <Chip
                    key={st}
                    label={label}
                    size="small"
                    color={color}
                    variant="filled"
                    sx={{fontSize: 11, height: 20}}
                />
            );
        })}
    </Stack>
);

const AliasesTab = ({
                        wikidataAliases = [],
                        surfaceForms = [],
                        loading,
                        onCreateAlias,
                        onDeleteAlias,
                        saving = false,
                        error,
                        crudEnabled = false,
                        pinEnabled = false,
                        pinnedAliases = [],
                        onPinAlias,
                        onUnpinAlias,
                        maxPinnedAliases = 5,
                    }) => {
    const [editorOpen, setEditorOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deletingAlias, setDeletingAlias] = useState(null);

    const hasKnown = wikidataAliases.length > 0;
    const hasSurface = surfaceForms && surfaceForms.length > 0;
    const {page, setPage, pageItems, totalPages, hasPagination} = usePagination(surfaceForms || []);
    const pinnedSet = new Set(
        (pinnedAliases || [])
            .filter((alias) => typeof alias === 'string' && alias.trim())
            .map((alias) => alias.trim().toLowerCase()),
    );
    const atPinCap = pinnedSet.size >= maxPinnedAliases;

    const handleAddClick = () => {
        setEditorOpen(true);
    };

    const handleDeleteClick = (alias) => {
        setDeletingAlias(alias);
        setDeleteOpen(true);
    };

    const handleEditorSubmit = async (alias) => {
        try {
            await onCreateAlias?.(alias);
            setEditorOpen(false);
        } catch (submitError) {
            // Keep dialog open so user can retry.
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deletingAlias) {
            setDeleteOpen(false);
            return;
        }

        try {
            await onDeleteAlias?.(deletingAlias);
            setDeleteOpen(false);
            setDeletingAlias(null);
        } catch (submitError) {
            // Keep dialog open so user can retry.
        }
    };

    if (loading) return <TabLoading/>;

    if (!hasKnown && !hasSurface) {
        return (
            <Stack>
                {crudEnabled && (
                    <Stack direction="row" justifyContent="space-between" alignItems="center" p={2}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Manage entity aliases
                            {pinEnabled ? ` · Pin up to ${maxPinnedAliases} on the card` : ''}
                        </Typography>
                        <Button variant="contained" size="small" onClick={handleAddClick} disabled={saving}>
                            Add alias
                        </Button>
                    </Stack>
                )}
                <TabEmpty message="No aliases found for this entity."/>
                {crudEnabled && (
                    <AliasEditorDialog
                        open={editorOpen}
                        saving={saving}
                        error={error}
                        onCancel={() => setEditorOpen(false)}
                        onSubmit={handleEditorSubmit}
                    />
                )}
            </Stack>
        );
    }

    return (
        <Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center" p={2}>
                <Typography variant="subtitle2" color="text.secondary">
                    {crudEnabled ? 'Manage entity aliases' : 'Entity aliases (read-only)'}
                    {pinEnabled ? ` · Pin up to ${maxPinnedAliases} on the card` : ''}
                </Typography>
                {crudEnabled && (
                    <Button variant="contained" size="small" onClick={handleAddClick} disabled={saving}>
                        Add alias
                    </Button>
                )}
            </Stack>

            {hasKnown && (
                <Box>
                    <Typography
                        variant="overline"
                        color="text.secondary"
                        sx={{px: 2, pb: 0.5, display: 'block', fontSize: 11}}
                    >
                        Known aliases · {wikidataAliases.length}
                    </Typography>
                    <Stack divider={<Divider sx={{mx: 2}}/>}>
                        {wikidataAliases.map((alias) => (
                            <KnownAliasRow
                                key={alias}
                                alias={alias}
                                crudEnabled={crudEnabled}
                                saving={saving}
                                onDeleteClick={handleDeleteClick}
                                pinEnabled={pinEnabled}
                                isPinned={pinnedSet.has(String(alias).trim().toLowerCase())}
                                atPinCap={atPinCap}
                                onPinAlias={onPinAlias}
                                onUnpinAlias={onUnpinAlias}
                            />
                        ))}
                    </Stack>
                </Box>
            )}

            {hasSurface && (
                <Box sx={hasKnown ? {mt: 1} : undefined}>
                    {hasKnown && <Divider/>}
                    <Typography
                        variant="overline"
                        color="text.secondary"
                        sx={{px: 2, pt: 1.5, pb: 0.5, display: 'block', fontSize: 11}}
                    >
                        Mentioned in corpus as · {surfaceForms.length} distinct form{surfaceForms.length !== 1 ? 's' : ''}
                    </Typography>
                    <Stack divider={<Divider sx={{mx: 2}}/>}>
                        {pageItems.map((form, i) => (
                            <SurfaceFormRow key={`${form.text}-${i}`} form={form}/>
                        ))}
                    </Stack>
                </Box>
            )}

            <TabPagination
                hasPagination={hasPagination}
                page={page}
                totalPages={totalPages}
                totalItems={surfaceForms?.length ?? 0}
                onChange={setPage}
            />

            {crudEnabled && (
                <AliasEditorDialog
                    open={editorOpen}
                    saving={saving}
                    error={error}
                    onCancel={() => setEditorOpen(false)}
                    onSubmit={handleEditorSubmit}
                />
            )}

            {crudEnabled && (
                <AliasDeleteDialog
                    open={deleteOpen}
                    alias={deletingAlias}
                    saving={saving}
                    onCancel={() => {
                        setDeleteOpen(false);
                        setDeletingAlias(null);
                    }}
                    onConfirm={handleDeleteConfirm}
                />
            )}
        </Stack>
    );
};

export default AliasesTab;
